import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { Plus, Trash, ArrowUp, ArrowDown, Funnel } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const CATEGORIES = {
  income: ['Salary', 'Freelance', 'Investment Returns', 'Business', 'Rental', 'Other'],
  expense: ['Food', 'Transport', 'Housing', 'Utilities', 'Entertainment', 'Shopping', 'Health', 'Education', 'Insurance', 'Other']
};

const fmt = (n) => `Rs. ${new Intl.NumberFormat('en-NP').format(Math.round(n || 0))}`;

export default function TransactionsPage() {
  const [transactions, setTransactions] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ type: 'expense', category: '', amount: '', description: '', date: new Date().toISOString().slice(0, 10), bank_id: '' });

  const load = () => {
    const params = filter !== 'all' ? { type: filter } : {};
    Promise.all([
      api.get('/transactions', { params }).then(r => setTransactions(r.data)),
      api.get('/banks').then(r => setBanks(r.data)),
    ]).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, [filter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/transactions', { ...form, amount: parseFloat(form.amount) });
      setDialogOpen(false);
      setForm({ type: 'expense', category: '', amount: '', description: '', date: new Date().toISOString().slice(0, 10), bank_id: '' });
      load();
    } catch {}
  };

  const handleDelete = async (id) => {
    await api.delete(`/transactions/${id}`);
    load();
  };

  const totalIncome = transactions.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0);
  const totalExpense = transactions.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="transactions-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-[#1C1B1A]" style={{ fontFamily: 'Manrope, sans-serif' }}>Transactions</h1>
          <p className="text-[#5C5A56] text-sm mt-1">Track your income and expenses</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <button data-testid="add-transaction-btn" className="bg-[#1E3F2D] text-white font-semibold rounded-xl px-5 py-2.5 hover:bg-[#29563E] transition-colors active:scale-95 flex items-center gap-2 text-sm">
              <Plus size={18} weight="bold" /> Add Transaction
            </button>
          </DialogTrigger>
          <DialogContent className="bg-white border-[#E8E6E1] rounded-2xl">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>New Transaction</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-2">
              <div className="flex gap-2">
                {['income', 'expense'].map(t => (
                  <button
                    key={t}
                    type="button"
                    data-testid={`type-${t}-btn`}
                    onClick={() => setForm({ ...form, type: t, category: '' })}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${form.type === t
                      ? (t === 'income' ? 'bg-[#265C42] text-white' : 'bg-[#A63C24] text-white')
                      : 'bg-[#FBF9F6] text-[#5C5A56] border border-[#E8E6E1]'
                    }`}
                  >
                    {t === 'income' ? 'Income' : 'Expense'}
                  </button>
                ))}
              </div>
              <div>
                <label htmlFor="transaction-category" className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-2">Category</label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger id="transaction-category" name="category" className="w-full border-[#E8E6E1] rounded-lg" data-testid="category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES[form.type].map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label htmlFor="transaction-amount" className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-2">Amount</label>
                <input
                  id="transaction-amount"
                  name="amount"
                  data-testid="amount-input"
                  type="number"
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  className="w-full bg-white border border-[#E8E6E1] text-[#1C1B1A] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1E3F2D]/20 focus:border-[#1E3F2D] transition-all"
                  placeholder="0.00"
                  required
                />
              </div>
              <div>
                <label htmlFor="transaction-description" className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-2">Description</label>
                <input
                  id="transaction-description"
                  name="description"
                  data-testid="description-input"
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full bg-white border border-[#E8E6E1] text-[#1C1B1A] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1E3F2D]/20 focus:border-[#1E3F2D] transition-all"
                  placeholder="e.g. Monthly grocery"
                />
              </div>
              <div>
                <label htmlFor="transaction-date" className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-2">Date</label>
                <input
                  id="transaction-date"
                  name="date"
                  data-testid="date-input"
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="w-full bg-white border border-[#E8E6E1] text-[#1C1B1A] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1E3F2D]/20 focus:border-[#1E3F2D] transition-all"
                  required
                />
              </div>
              {banks.length > 0 && (
                <div>
                  <label htmlFor="transaction-bank" className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-2">Account / Wallet</label>
                  <Select value={form.bank_id} onValueChange={(v) => setForm({ ...form, bank_id: v === 'none' ? '' : v })}>
                    <SelectTrigger id="transaction-bank" name="bank_id" className="w-full border-[#E8E6E1] rounded-lg">
                      <SelectValue placeholder="Select money source" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {banks.map(b => (
                        <SelectItem key={b.id} value={b.id}>
                          {b.bank_name} ({fmt(b.balance)})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <button data-testid="submit-transaction-btn" type="submit" className="w-full bg-[#1E3F2D] text-white font-semibold rounded-xl px-6 py-3 hover:bg-[#29563E] transition-colors active:scale-95">
                Add Transaction
              </button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-4 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#5C5A56] mb-1">Income</p>
          <p className="text-lg font-bold font-mono-numbers text-[#265C42]" data-testid="total-income">{fmt(totalIncome)}</p>
        </div>
        <div className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-4 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#5C5A56] mb-1">Expenses</p>
          <p className="text-lg font-bold font-mono-numbers text-[#A63C24]" data-testid="total-expenses">{fmt(totalExpense)}</p>
        </div>
        <div className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-4 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#5C5A56] mb-1">Net</p>
          <p className={`text-lg font-bold font-mono-numbers ${totalIncome - totalExpense >= 0 ? 'text-[#265C42]' : 'text-[#A63C24]'}`} data-testid="net-amount">
            {fmt(totalIncome - totalExpense)}
          </p>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        <Funnel size={16} className="text-[#5C5A56]" />
        {['all', 'income', 'expense'].map(f => (
          <button
            key={f}
            data-testid={`filter-${f}`}
            onClick={() => setFilter(f)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${filter === f ? 'bg-[#1E3F2D] text-white' : 'bg-white border border-[#E8E6E1] text-[#5C5A56] hover:bg-[#FBF9F6]'}`}
          >
            {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Transaction list */}
      <div className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm divide-y divide-[#E8E6E1]" data-testid="transaction-list">
        {loading ? (
          <div className="p-8 text-center"><div className="w-6 h-6 border-2 border-[#1E3F2D] border-t-transparent rounded-full animate-spin mx-auto" /></div>
        ) : transactions.length === 0 ? (
          <div className="p-8 text-center text-[#5C5A56] text-sm">No transactions found</div>
        ) : (
          transactions.map(t => (
            <div key={t.id} className="flex items-center justify-between p-4 hover:bg-[#FBF9F6] transition-colors" data-testid={`transaction-${t.id}`}>
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${t.type === 'income' ? 'bg-[#265C42]/10' : 'bg-[#A63C24]/10'}`}>
                  {t.type === 'income' ? <ArrowUp size={16} className="text-[#265C42]" weight="bold" /> : <ArrowDown size={16} className="text-[#A63C24]" weight="bold" />}
                </div>
                <div>
                  <p className="text-sm font-medium text-[#1C1B1A]">{t.description || t.category}</p>
                  <p className="text-xs text-[#5C5A56]">{t.category} &middot; {t.date?.slice(0, 10)}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`font-mono-numbers text-sm font-semibold ${t.type === 'income' ? 'text-[#265C42]' : 'text-[#A63C24]'}`}>
                  {t.type === 'income' ? '+' : '-'}{fmt(t.amount)}
                </span>
                <button onClick={() => handleDelete(t.id)} className="text-[#5C5A56] hover:text-[#A63C24] transition-colors p-1" data-testid={`delete-${t.id}`}>
                  <Trash size={16} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
